"use client";
import { useState, useCallback, useEffect } from "react";

export interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface HistoryEntry {
  id: string;
  timestamp: string;
  status: "pending" | "approved" | "rejected" | "paid";
  user: string;
  action: string;
}

export interface Note {
  id: string;
  timestamp: string;
  author: string;
  content: string;
}

export interface DetailInvoice {
  id: string;
  number: string;
  vendor: string;
  amount: number;
  status: "pending" | "approved" | "rejected" | "paid";
  dueDate: string;
  category: string;
  description: string;
  createdDate: string;
  lineItems: LineItem[];
  history: HistoryEntry[];
  notes: Note[];
}

const generateMockDetailInvoice = (id: string): DetailInvoice => {
  const createdDate = new Date(2024, 0, 15).toISOString().split("T")[0];
  const dueDate = new Date(2024, 1, 15).toISOString().split("T")[0];

  return {
    id,
    number: `INV-2024-${String(Math.floor(Math.random() * 10000)).padStart(4, "0")}`,
    vendor: "Acme Corp",
    amount: 15450.0,
    status: "approved",
    dueDate,
    category: "Operations",
    description:
      "Professional services for Q1 2024 operations review and optimization",
    createdDate,
    lineItems: [
      {
        id: "li-1",
        description: "Consulting services - 40 hours @ $150/hr",
        quantity: 40,
        unitPrice: 150,
        total: 6000,
      },
      {
        id: "li-2",
        description: "Software license renewal",
        quantity: 1,
        unitPrice: 5450,
        total: 5450,
      },
      {
        id: "li-3",
        description: "Implementation support - 10 hours @ $400/hr",
        quantity: 10,
        unitPrice: 400,
        total: 4000,
      },
    ],
    history: [
      {
        id: "h-1",
        timestamp: new Date(2024, 0, 15, 10, 30).toISOString(),
        status: "pending",
        user: "John Smith",
        action: "Invoice created",
      },
      {
        id: "h-2",
        timestamp: new Date(2024, 0, 18, 14, 15).toISOString(),
        status: "approved",
        user: "Sarah Johnson",
        action: "Invoice approved",
      },
    ],
    notes: [
      {
        id: "n-1",
        timestamp: new Date(2024, 0, 15, 11, 0).toISOString(),
        author: "John Smith",
        content:
          "Please review this invoice for the Q1 operations consulting engagement.",
      },
      {
        id: "n-2",
        timestamp: new Date(2024, 0, 18, 13, 45).toISOString(),
        author: "Sarah Johnson",
        content:
          "Approved. All items verified against contract terms. Ready for payment processing.",
      },
    ],
  };
};

export interface DetailController {
  invoice: DetailInvoice | null;
  loading: boolean;
  activeTab: "overview" | "lineItems" | "history" | "notes";
  setActiveTab: (tab: "overview" | "lineItems" | "history" | "notes") => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  isDirty: boolean;
  setIsDirty: (dirty: boolean) => void;
  formData: Partial<DetailInvoice>;
  setFormField: (field: string, value: unknown) => void;
  saveChanges: () => Promise<void>;
  discardChanges: () => void;
  addNote: (content: string) => void;
  deleteNote: (noteId: string) => void;
}

export function useDetailController(invoiceId: string): DetailController {
  const [invoice, setInvoice] = useState<DetailInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<
    "overview" | "lineItems" | "history" | "notes"
  >("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [formData, setFormData] = useState<Partial<DetailInvoice>>({});

  // Load invoice data on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      const mockInvoice = generateMockDetailInvoice(invoiceId);
      setInvoice(mockInvoice);
      setFormData(mockInvoice);
      setLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [invoiceId]);

  const setFormField = useCallback(
    (field: string, value: unknown) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
      setIsDirty(true);
    },
    []
  );

  const saveChanges = useCallback(async () => {
    setLoading(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 800));

    setInvoice((prev) =>
      prev ? { ...prev, ...formData } : null
    );
    setIsDirty(false);
    setIsEditing(false);
    setLoading(false);
  }, [formData]);

  const discardChanges = useCallback(() => {
    if (invoice) {
      setFormData(invoice);
    }
    setIsDirty(false);
    setIsEditing(false);
  }, [invoice]);

  const addNote = useCallback(
    (content: string) => {
      if (!invoice) return;

      const newNote: Note = {
        id: `n-${Date.now()}`,
        timestamp: new Date().toISOString(),
        author: "Current User",
        content,
      };

      setInvoice((prev) =>
        prev
          ? {
              ...prev,
              notes: [newNote, ...prev.notes],
            }
          : null
      );
      setFormData((prev) => ({
        ...prev,
        notes: [newNote, ...(prev.notes || [])],
      }));
    },
    [invoice]
  );

  const deleteNote = useCallback((noteId: string) => {
    setInvoice((prev) =>
      prev
        ? {
            ...prev,
            notes: prev.notes.filter((n) => n.id !== noteId),
          }
        : null
    );
    setFormData((prev) => ({
      ...prev,
      notes: (prev.notes || []).filter((n) => n.id !== noteId),
    }));
  }, []);

  return {
    invoice,
    loading,
    activeTab,
    setActiveTab,
    isEditing,
    setIsEditing,
    isDirty,
    setIsDirty,
    formData,
    setFormField,
    saveChanges,
    discardChanges,
    addNote,
    deleteNote,
  };
}
